import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import classNames from 'classnames';
import ScrollToBottom from 'react-scroll-to-bottom';

import { API } from 'constants';
import client from '../../../../services/assistant-api';

import { useUserChatMessage } from '../../../../hooks/useUserChatMessage';
import { useManageSearchParams } from '../../../../hooks/useManageSearchParams';
import { useAssistantMessageStream } from '../../../../hooks/useAssistantMessageStream';

import { TEST_VIEW } from '../../../../constants/bot';
import { CHAT_GPT4o_MODEL } from '../../../AssistantPage/AIModelSelector/AIModelSelector';
import { CHAT_ROLE, CHAT_TYPE } from '../../../../constants/assistant';
import { CHAT_SEARCH_PARAM_KEY } from '../BotPreviewPanel/BotPreviewPanel';

import Loading from '../../../../components/Loading';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import ChatInputBox from '../BotPreviewPanel/ChatInputBox/ChatInputBox';
import ChatContent from '../../../AssistantPage/ChatContent/ChatContent';
import ChatInvitingView from '../../../AssistantPage/ChatInvitingView/ChatInvitingView';

const AssistantPreview = ({
    botId,
    botName,
    isVisible,
    isPrevChatLoading,
    setIsPrevChatLoading,
    panelContainerRef,
    isInstructionEditing,
}) => {
    const [searchParams] = useSearchParams();
    const chatThreadId = searchParams.get(CHAT_SEARCH_PARAM_KEY) || null;

    const [chatMessages, setChatMessages] = useState([]);
    const [messageStreamState, setMessageStreamState] = useState({
        isAssistantLoading: false,
        isStreamRunning: false,
    });

    const { setSearchParam, deleteSearchParam } = useManageSearchParams();

    useEffect(() => {
        const fetchChatThreadData = async () => {
            try {
                setIsPrevChatLoading(true);
                const { data } = await client.get(
                    `${API.ROUTES.assistant.chatThread}${chatThreadId}/`
                );
                setChatMessages(data.chat_messages || []);
            } catch (e) {
                deleteSearchParam(CHAT_SEARCH_PARAM_KEY);
            } finally {
                setIsPrevChatLoading(false);
            }
        };

        if (chatThreadId) {
            void fetchChatThreadData();
        }
    }, []);

    const modelSettings = {
        type: CHAT_TYPE.custom,
        model: CHAT_GPT4o_MODEL,
        custom_bot_version: { custom_bot: { id: botId, name: botName } },
    };
    const {
        startListeningWebSockets,
        stopWebSocketStream,
        tryReconnectIfWebsocketClosed,
        clearMessageLoadingStreamState,
        resetAssistantMessageResponse,
        cancelStream,
        isStreamCanceled,
        emptyCanceledAssistantMessage,
        setEmptyCanceledAssistantMessage,
    } = useAssistantMessageStream({
        setChatMessages,
        messageStreamState,
        setMessageStreamState,
        modelSettings,
    });

    const getSubmittingMessageRequestBody = ({ message, isNewChat }) => {
        const requestBody = {
            content: message,
            origin: 'test',
            type: CHAT_TYPE.custom,
            custom_bot: botId,
        };
        if (!isNewChat) {
            requestBody.chat_thread = chatThreadId;
        }

        return requestBody;
    };

    const getRegenerateMessageRequestBody = useCallback(
        ({ role, message }) => {
            const requestBody = {
                regenerate: true,
                origin: 'test',
                type: CHAT_TYPE.custom,
                custom_bot: botId,
            };
            if (role === CHAT_ROLE.user) {
                requestBody.content = message;
            }

            return requestBody;
        },
        [botId]
    );

    const handleMessageSubmitResponse = useCallback(
        ({ data, messagesToAppend, isNewChat }) => {
            if (!isNewChat) {
                setChatMessages((prevMessages) => [...prevMessages, ...messagesToAppend]);
            }

            if (isNewChat) {
                const id = data.chat_thread.id;
                setSearchParam(CHAT_SEARCH_PARAM_KEY, id);
                setChatMessages([...messagesToAppend]);
            }
        },
        [setChatMessages, setSearchParam]
    );

    const {
        submitChatMessage,
        handleRegenerateChatMessage,
        displayedChatMessages,
        setSubmittedUserMessage,
        messageSubmissionErrorData,
        setMessageSubmissionErrorData,
        controller,
        errorAlert,
        setErrorAlert,
        scrollPosition,
        setScrollPosition,
        containerRef,
    } = useUserChatMessage({
        chatMessages,
        setChatMessages,
        setMessageStreamState,
        isNewChat: !chatThreadId,
        handleMessageSubmitResponse,
        tryReconnectIfWebsocketClosed,
        startListeningWebSockets,
        getSubmittingMessageRequestBody,
        getRegenerateMessageRequestBody,
        emptyCanceledAssistantMessage,
        setEmptyCanceledAssistantMessage,
        isStreamCanceled,
    });

    useEffect(() => {
        if (chatThreadId) return;

        refreshChat();
    }, [chatThreadId]);

    const refreshChat = () => {
        stopWebSocketStream();
        controller.current?.abort();

        isStreamCanceled.current = false;
        setEmptyCanceledAssistantMessage(null);

        setChatMessages([]);
        setSubmittedUserMessage(null);
        resetAssistantMessageResponse();
        clearMessageLoadingStreamState();
    };

    const chatMessagesExist = !!displayedChatMessages?.length;

    const containerClassName = classNames(
        'w-full max-w-[1010px] mx-auto min-h-full',
        !chatMessagesExist && 'h-full',
        chatMessagesExist && 'pb-6'
    );

    const chatWrapperClassName = classNames(
        'flex-1 overflow-hidden',
        (isPrevChatLoading || !isVisible) && 'hidden'
    );

    const isAssistantMessageLoadingOrStreaming =
        messageStreamState.isAssistantLoading || messageStreamState.isStreamRunning;

    const isRegenerateMessageDisabled =
        isAssistantMessageLoadingOrStreaming || !!isStreamCanceled.current || isInstructionEditing;

    const isSendButtonDisabled =
        !!isStreamCanceled.current || !!isInstructionEditing || isPrevChatLoading;

    return (
        <>
            <div className={chatWrapperClassName} ref={containerRef}>
                <ScrollToBottom
                    className="h-full"
                    followButtonClassName="hidden"
                    initialScrollBehavior="auto"
                >
                    <div className="px-5 3xl:px-8 pt-5 pb-6 h-full">
                        <div className={containerClassName}>
                            {chatMessagesExist && (
                                <ChatContent
                                    chatMessages={displayedChatMessages}
                                    setChatMessages={setChatMessages}
                                    chatType={CHAT_TYPE.custom}
                                    isRegenerateMessageDisabled={isRegenerateMessageDisabled}
                                    handleRegenerateChatMessage={handleRegenerateChatMessage}
                                    messageStreamState={messageStreamState}
                                    scrollPosition={scrollPosition}
                                    setScrollPosition={setScrollPosition}
                                    containerRef={containerRef}
                                    isSimplifiedVersion
                                />
                            )}

                            {!chatMessagesExist && (
                                <ChatInvitingView
                                    chatType={CHAT_TYPE.custom}
                                    selectedCustomBot={{ name: botName }}
                                    isSimplifiedVersion
                                />
                            )}
                        </div>
                    </div>
                </ScrollToBottom>
            </div>

            {isPrevChatLoading && isVisible && (
                <div className="flex-1 flex items-center justify-center pb-9">
                    <Loading withText={false} />
                </div>
            )}

            <ChatInputBox
                isVisible={isVisible}
                view={TEST_VIEW.assistant}
                submitChatMessage={submitChatMessage}
                handleCancelStream={cancelStream}
                isLoading={isAssistantMessageLoadingOrStreaming}
                isSendButtonDisabled={isSendButtonDisabled}
                messageErrorData={messageSubmissionErrorData}
                setMessageErrorData={setMessageSubmissionErrorData}
                botName={botName}
                chatThreadId={chatThreadId}
                containerRef={panelContainerRef}
            />

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </>
    );
};

export default AssistantPreview;
